import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const mockMessages = [
  { sender: 'user', text: 'Hi, can I get an update on my request?', time: '10:00 AM' },
  { sender: 'admin', text: 'Sure! We are working on it and will deliver soon.', time: '10:05 AM' },
];

const mockFiles = [
  { name: 'Logo_v1.png', url: '#' },
  { name: 'Final_Design.pdf', url: '#' },
];

const tabList = [
  { key: 'details', label: 'Details' },
  { key: 'messages', label: 'Messages' },
  { key: 'files', label: 'Files' },
];

const RequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { answers } = location.state || { answers: {} };
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [brandName, setBrandName] = useState<string>('');

  useEffect(() => {
    const fetchRequest = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          brands!inner(name)
        `)
        .eq('id', id)
        .single();
      
      if (!error && data) {
        setRequest(data);
        setBrandName(data.brands?.name || 'Unknown Brand');
      }
      setLoading(false);
    };
    if (id) fetchRequest();
  }, [id]);

  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from('request_message')
        .select('*')
        .eq('request_id', id)
        .order('id', { ascending: false });
      if (!error) setMessages(data || []);
    };
    if (id) fetchMessages();
  }, [id]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !file) || !user) return;

    let fileUrl = '';
    if (file) {
      const { data: fileData, error: fileError } = await supabase.storage
        .from('messages')
        .upload(`${id}/${file.name}`, file);
      if (!fileError) {
        const { data: { publicUrl } } = supabase.storage
          .from('messages')
          .getPublicUrl(`${id}/${file.name}`);
        fileUrl = publicUrl;
      }
    }

    const { error } = await supabase
      .from('request_message')
      .insert([
        { request_id: id, message: newMessage, sender: 'user', sender_id: user.id, file_url: fileUrl }
      ]);

    if (!error) {
      setNewMessage('');
      setFile(null);
      const { data } = await supabase
        .from('request_message')
        .select('*')
        .eq('request_id', id)
        .order('id', { ascending: true });
      setMessages(data || []);
    }
  };

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-xl shadow border p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Request Details</h1>
      <div className="flex space-x-4 border-b mb-6">
        {tabList.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`pb-2 px-4 font-semibold border-b-2 transition-colors ${activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-primary'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          {activeTab === 'details' && (
            <div>
              {request ? (
                <div className="space-y-4">
                  <div>
                    <span className="font-semibold">Request Type:</span> {request.type.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </div>
                  <div>
                    <span className="font-semibold">Brand:</span> {brandName}
                  </div>
                  <div>
                    <span className="font-semibold">Questions and Answers:</span>
                    <div className="mt-2 pl-4">
                      {request.questions_and_answers ? (
                        // New structured format
                        request.questions_and_answers.map((qa: any, index: number) => (
                          <div key={index} className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <div className="font-semibold text-gray-800 mb-1">{qa.question}</div>
                            <div className="text-gray-600">
                              {qa.answer ? (
                                qa.fieldType === 'file' ? (
                                  <a href={qa.answer} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                    View File
                                  </a>
                                ) : (
                                  String(qa.answer)
                                )
                              ) : (
                                <span className="text-gray-400 italic">No answer provided</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Field: {qa.fieldName} | Type: {qa.fieldType} {qa.required && '| Required'}
                            </div>
                          </div>
                        ))
                      ) : (
                        // Fallback to old format
                        Object.entries(answers).map(([question, answer]) => (
                          <div key={question} className="mb-2">
                            <span className="font-semibold capitalize">{question.replace(/_/g, ' ')}:</span> {String(answer)}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500">Request not found.</div>
              )}
            </div>
          )}
          {activeTab === 'messages' && (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`rounded-lg px-4 py-2 max-w-xs ${msg.sender === 'user' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-900'}`}>
                    <div className="text-sm">{msg.message}</div>
                    {msg.file_url && (
                      <div className="mt-2">
                        {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <img src={msg.file_url} alt="attachment" className="max-w-xs max-h-40 rounded mb-2" />
                        ) : null}
                        <a href={msg.file_url} download className="text-sm underline block">Download File</a>
                      </div>
                    )}
                    <div className="text-xs mt-1 text-right">{new Date(msg.created_at).toLocaleTimeString()}</div>
                  </div>
                </div>
              ))}
              <form onSubmit={handleSendMessage} className="flex mt-4 space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border rounded-md focus:ring-primary focus:border-primary"
                />
                <input
                  type="file"
                  id="file-upload"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-md cursor-pointer transition-colors"
                  title="Attach file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </label>
                <button type="submit" className="bg-primary text-white px-4 py-2 rounded-md font-semibold hover:bg-primary/90">Send</button>
              </form>
            </div>
          )}
          {activeTab === 'files' && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Files</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {messages.filter(msg => msg.file_url).map((msg, idx) => (
                  <div key={msg.id || idx} className="bg-gray-100 rounded-lg p-3 flex flex-col items-center">
                    {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                      <img src={msg.file_url} alt="attachment" className="max-h-32 mb-2 rounded" />
                    ) : (
                      <div className="flex flex-col items-center mb-2">
                        <span className="material-icons text-4xl text-gray-400">insert_drive_file</span>
                        <span className="text-xs mt-1">{msg.file_url.split('/').pop()}</span>
                      </div>
                    )}
                    <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs mt-2">Download</a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RequestDetails; 